// Google OAuth Service for handling Google Sign-In
// Uses Google Identity Services (GIS) ID token method for modern OAuth implementation
// This approach avoids COOP issues and is more secure

import { getGoogleClientId, getServerUrl } from '../config/runtimeConfig';

class GoogleOAuthService {
  constructor() {
    this.clientId = getGoogleClientId();
    this.isInitialized = false;
    this.googleScript = null;
  }

  /**
   * Initialize Google OAuth by loading the Google Identity Services script
   */
  async initialize() {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.getElementById('google-oauth-script')) {
        this.isInitialized = true;
        resolve();
        return;
      }

      // Create and load the Google Identity Services script
      const script = document.createElement('script');
      script.id = 'google-oauth-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        this.isInitialized = true;
        console.log('Google OAuth script loaded successfully');
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Google OAuth script:', error);
        reject(new Error('Failed to load Google OAuth script'));
      };
      
      document.head.appendChild(script);
      this.googleScript = script;
    });
  }

  /**
   * Check if Google OAuth is available and properly configured
   */
  isAvailable() {
    // Check if we have the minimum requirements
    const hasClientId = !!(this.clientId);
    const hasScript = !!(document.getElementById('google-oauth-script') || this.isInitialized);
    const hasGoogleAPI = !!(window.google && window.google.accounts);
    
    console.log('Google OAuth availability check:', {
      hasClientId,
      hasScript,
      hasGoogleAPI,
      isInitialized: this.isInitialized
    });
    
    return hasClientId && hasScript && hasGoogleAPI;
  }

  /**
   * Handle Google Sign-In using ID token method (COOP-friendly)
   * This method uses the credential callback which provides an ID token directly
   */
  async signInWithCredential() {
    try {
      await this.initialize();
      
      if (!this.clientId) {
        throw new Error('Google Client ID is not configured');
      }

      return new Promise((resolve, reject) => {
        if (!window.google || !window.google.accounts) {
          reject(new Error('Google OAuth not initialized'));
          return;
        }

        // Initialize with ID token callback
        window.google.accounts.id.initialize({
          client_id: this.clientId,
          callback: (response) => {
            if (response.error) {
              reject(new Error(response.error));
              return;
            }
            
            console.log('Google ID token received');
            resolve({
              credential: response.credential,
              clientId: this.clientId
            });
          },
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Try to show the One Tap prompt first
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('One Tap not available, showing button');
            // Fallback to button method
            this.showSignInButton().then(resolve).catch(reject);
          }
        });
      });
    } catch (error) {
      throw new Error(`Google Sign-In failed: ${error.message}`);
    }
  }

  /**
   * Show Google Sign-In button as fallback
   */
  showSignInButton() {
    return new Promise((resolve, reject) => {
      // Create modal overlay
      const overlay = document.createElement('div');
      overlay.id = 'google-signin-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      `;

      // Create modal content
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        text-align: center;
        max-width: 400px;
        width: 90%;
      `;

      // Add title
      const title = document.createElement('h3');
      title.textContent = 'Sign in with Google';
      title.style.cssText = `
        margin: 0 0 20px 0;
        color: #333;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;

      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.id = 'google-signin-button';
      buttonContainer.style.cssText = `
        margin: 20px 0;
        display: flex;
        justify-content: center;
      `;

      // Add cancel button
      const cancelButton = document.createElement('button');
      cancelButton.textContent = 'Cancel';
      cancelButton.style.cssText = `
        margin-top: 20px;
        padding: 8px 16px;
        border: 1px solid #ccc;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      cancelButton.onclick = () => {
        document.body.removeChild(overlay);
        reject(new Error('User cancelled sign-in'));
      };

      // Close on overlay click
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          reject(new Error('User cancelled sign-in'));
        }
      };

      // Assemble modal
      modal.appendChild(title);
      modal.appendChild(buttonContainer);
      modal.appendChild(cancelButton);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Initialize Google Sign-In button
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: (response) => {
          document.body.removeChild(overlay);
          if (response.error) {
            reject(new Error(response.error));
            return;
          }
          resolve({
            credential: response.credential,
            clientId: this.clientId
          });
        }
      });

      // Render the button
      window.google.accounts.id.renderButton(buttonContainer, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: 250
      });
    });
  }

  /**
   * Process Google login with backend
   * @param {Object} googleResponse - Response from Google OAuth
   * @returns {Promise<Object>} Backend response
   */
  async processGoogleLogin(googleResponse) {
    try {
      const response = await fetch(`${getServerUrl()}/api/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: googleResponse.credential,
          clientId: googleResponse.clientId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Google login failed');
      }

      return data;
    } catch (error) {
      console.error('Google login processing error:', error);
      throw error;
    }
  }

  /**
   * Complete Google Sign-In process
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async signIn(onSuccess, onError) {
    try {
      console.log('Starting Google Sign-In process...');
      
      // Get Google credential
      const googleResponse = await this.signInWithCredential();
      console.log('Google credential received');

      // Process with backend
      const backendResponse = await this.processGoogleLogin(googleResponse);
      console.log('Backend processing completed');

      // Call success callback
      onSuccess(backendResponse);
    } catch (error) {
      console.error('Google Sign-In error:', error);
      onError(error);
    }
  }

  /**
   * Clean up Google OAuth resources
   */
  cleanup() {
    if (this.googleScript) {
      document.head.removeChild(this.googleScript);
      this.googleScript = null;
    }
    this.isInitialized = false;
  }
}

// Export singleton instance
const googleOAuthService = new GoogleOAuthService();
export default googleOAuthService;
